import { useState } from 'react';
import {
  Box, Paper, TextField, Button, Typography,
  Alert, CircularProgress, InputAdornment, IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../shared/contexts/AuthContext';
import { DEFAULT_CATALOGUE_SETTINGS } from '../shared/types';
import { API_BASE_URL } from '../shared/services/apiClient';

export const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const settings = DEFAULT_CATALOGUE_SETTINGS;
  const logoUrl = `${API_BASE_URL}/api/branding/logo/${encodeURIComponent(settings.logo_file)}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username.trim() || !password) return;
    setLoading(true);
    setError('');
    try {
      await login(username.trim(), password);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('login.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#F7F6F2',
        p: 2,
      }}
    >
      <Paper
        elevation={3}
        sx={{ p: 4, width: '100%', maxWidth: 380, borderRadius: 2 }}
        component="form"
        onSubmit={handleSubmit}
      >
        {/* Logo */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box
            component="img"
            src={logoUrl}
            alt="Brand logo"
            sx={{ height: 64, objectFit: 'contain' }}
            onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
        </Box>

        <Typography variant="h6" align="center" gutterBottom sx={{ color: '#363D4C', fontWeight: 700 }}>
          {t('login.title')}
        </Typography>
        <Typography variant="body2" align="center" sx={{ color: '#777', mb: 3 }}>
          {t('login.subtitle')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          label={t('login.username')}
          value={username}
          onChange={e => setUsername(e.target.value)}
          fullWidth
          autoFocus
          autoComplete="username"
          sx={{ mb: 2 }}
          disabled={loading}
        />

        <TextField
          label={t('login.password')}
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={e => setPassword(e.target.value)}
          fullWidth
          autoComplete="current-password"
          sx={{ mb: 3 }}
          disabled={loading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword(v => !v)} edge="end" size="small">
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={loading || !username.trim() || !password}
          sx={{
            bgcolor: '#A55DA0',
            '&:hover': { bgcolor: '#8d4a88' },
            fontWeight: 700,
          }}
        >
          {loading ? <CircularProgress size={22} sx={{ color: '#fff' }} /> : t('login.signin')}
        </Button>
      </Paper>
    </Box>
  );
};

export default LoginPage;
